import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MapPin, Mail, Instagram, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', service: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    { value: 'closet-cleanse', label: 'Closet Cleanse' },
    { value: 'style-foundation', label: 'Style Foundation Package' },
    { value: 'personal-shopping', label: 'Personal Shopping' },
    { value: 'capsule-wardrobe', label: 'Capsule Wardrobe' },
    { value: 'interior-consultation', label: 'Interior Design Consultation' },
    { value: 'room-refresh', label: 'Room Refresh' },
    { value: 'full-home-design', label: 'Full Home Design' },
    { value: 'event-styling', label: 'Event Styling' },
    { value: 'other', label: 'Other / Not Sure' }
  ];

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left Side - Info */}
            <div>
              <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-6">
                Get In Touch
              </p>
              <h1 className="font-serif text-5xl md:text-6xl text-[#2C3E50] mb-8 leading-tight">
                Let's Create Something Beautiful
              </h1>
              <p className="font-sans text-[#57534E] text-lg leading-relaxed mb-12">
                Ready to transform your wardrobe or living space? I'd love to hear from you. 
                Fill out the form and I'll get back to you within 24-48 hours.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4A373]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-[#D4A373]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#2C3E50] mb-1">Location</h3>
                    <p className="font-sans text-[#57534E]">San Diego, California</p>
                    <p className="font-sans text-[#57534E] text-sm">Serving all of San Diego County</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4A373]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-[#D4A373]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#2C3E50] mb-1">Email</h3>
                    <a 
                      href="mailto:hello@maisonloulou.co"
                      className="font-sans text-[#2C3E50] hover:text-[#D4A373] transition-colors"
                    >
                      hello@maisonloulou.co
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4A373]/10 flex items-center justify-center flex-shrink-0">
                    <Instagram className="h-5 w-5 text-[#D4A373]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#2C3E50] mb-1">Instagram</h3>
                    <a 
                      href="https://instagram.com/maisonloulou"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-sans text-[#2C3E50] hover:text-[#D4A373] transition-colors"
                    >
                      @maisonloulou
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
              {isSubmitted ? (
                <div data-testid="form-success" className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-[#84A98C] mx-auto mb-6" />
                  <h3 className="font-serif text-3xl text-[#2C3E50] mb-4">
                    Message Sent!
                  </h3>
                  <p className="font-sans text-[#57534E] mb-8">
                    Thank you for reaching out. I'll get back to you within 24-48 hours.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="rounded-full border-[#2C3E50] text-[#2C3E50] hover:bg-[#2C3E50] hover:text-white"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-sans text-[#2C3E50]">
                      Your Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-[#F9F7F2] border-0 rounded-xl py-6 px-4 font-sans focus:ring-2 focus:ring-[#D4A373]"
                      placeholder="Jane Doe"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-sans text-[#2C3E50]">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-[#F9F7F2] border-0 rounded-xl py-6 px-4 font-sans focus:ring-2 focus:ring-[#D4A373]"
                      placeholder="jane@example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service" className="font-sans text-[#2C3E50]">
                      Service Interest
                    </Label>
                    <Select onValueChange={handleServiceChange} value={formData.service}>
                      <SelectTrigger 
                        data-testid="contact-service-select"
                        className="bg-[#F9F7F2] border-0 rounded-xl py-6 px-4 font-sans focus:ring-2 focus:ring-[#D4A373]"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem 
                            key={service.value} 
                            value={service.value}
                            className="font-sans"
                          >
                            {service.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-sans text-[#2C3E50]">
                      Your Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-[#F9F7F2] border-0 rounded-xl py-4 px-4 font-sans focus:ring-2 focus:ring-[#D4A373] resize-none"
                      placeholder="Tell me about your style goals or project..."
                    />
                  </div>

                  {error && (
                    <p data-testid="contact-error" className="text-red-500 text-sm font-sans">
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full rounded-full bg-[#2C3E50] hover:bg-[#1a252f] text-white py-6 text-base transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map/Image Section */}
      <section className="relative h-96">
        <img
          src="https://images.unsplash.com/photo-1554496609-3599bdd89627?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          alt="San Diego coastline"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2C3E50]/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h2 className="font-serif text-4xl md:text-5xl mb-4">San Diego, California</h2>
            <p className="font-sans text-white/80">Serving all of beautiful San Diego County</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
